import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { MapPin, Phone, ArrowRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

// Fallback service areas data
const fallbackServiceData = {
  coverage: {
    primary: ["Casselberry", "Winter Park", "Oviedo", "Altamonte Springs", "Longwood"],
    secondary: ["Orlando", "Lake Mary", "Sanford", "Apopka", "Maitland"],
    headquarters: {
      city: "Orlando",
      state: "FL",
      address: "Central Florida",
      zip: "32801",
    },
  },
  regions: [
    { name: "Seminole County", states: ["Casselberry", "Winter Park", "Oviedo", "Altamonte Springs", "Longwood"] },
    { name: "Orange County", states: ["Orlando", "Apopka", "Maitland", "Winter Garden"] },
    { name: "North Orlando", states: ["Lake Mary", "Sanford", "Heathrow"] },
  ],
};

export const ServiceArea = () => {
  const [serviceData, setServiceData] = useState(fallbackServiceData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchServiceAreas = async () => {
      if (!API) return; // No backend, use fallback data already set
      try {
        const response = await axios.get(`${API}/service-areas`);
        if (response.data?.coverage) {
          setServiceData(response.data);
        }
      } catch (error) {
        // Keep using fallback data
        console.log("Using fallback service areas data");
      }
    };
    fetchServiceAreas();
  }, []);

  return (
    <section
      data-testid="service-area-section"
      className="py-20 sm:py-28 bg-white relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="text-center mb-12 sm:mb-14">
          <span className="inline-block text-xs sm:text-sm font-medium uppercase tracking-widest text-brand-orange mb-3">
            Where We Work
          </span>
          <h2
            data-testid="service-area-heading"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight uppercase text-brand-navy"
          >
            Service Coverage
          </h2>
          <p className="font-body text-sm sm:text-base text-brand-slate mt-3 max-w-xl mx-auto">
            Serving communities across Central Florida with quality residential painting services
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
          {/* Google Map */}
          <div className="relative">
            <div className="h-[400px] bg-brand-ice rounded-sm border border-border overflow-hidden shadow-sm">
              <iframe
                title="Roll Orlando Service Area"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224444.95033584045!2d-81.57960064453125!3d28.481168699999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e773d8fecdbc77%3A0xac3b2063ca5bf9e!2sOrlando%2C%20FL!5e0!3m2!1sen!2sus!4v1635180000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Coverage Info */}
          <div className="space-y-6">
            {/* Service Center */}
            <div className="bg-brand-navy rounded-xl p-5 text-white">
              <h3 className="font-heading text-lg font-semibold uppercase mb-3">
                Service Center
              </h3>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">
                    Orlando, Florida
                  </p>
                  <p className="text-white/60 text-sm">
                    Serving Central FL
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/10">
                <Phone className="w-4 h-4 text-brand-orange" />
                <a
                  href="tel:+14079494656"
                  className="text-sm hover:text-brand-orange transition-colors"
                >
                  (407) 949-4656
                </a>
              </div>
            </div>

            {/* Primary Service Areas */}
            <div>
              <h3 className="font-heading text-base font-semibold uppercase text-brand-navy mb-2">
                Primary Service Areas
              </h3>
              <div className="flex flex-wrap gap-2">
                {serviceData?.coverage?.primary?.map((state) => (
                  <Badge
                    key={state}
                    className="bg-brand-orange/10 text-brand-navy border border-brand-orange/20 font-heading uppercase px-3 py-1.5 text-xs"
                  >
                    {state}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Extended Coverage */}
            <div>
              <h3 className="font-heading text-base font-semibold uppercase text-brand-navy mb-2">
                Extended Coverage
              </h3>
              <div className="flex flex-wrap gap-2">
                {serviceData?.coverage?.secondary?.map((state) => (
                  <Badge
                    key={state}
                    variant="outline"
                    className="border-gray-200 text-brand-slate font-heading uppercase px-3 py-1.5 text-xs"
                  >
                    {state}
                  </Badge>
                ))}
              </div>
            </div>

            {/* CTA */}
            <Link to="/contact">
              <Button
                data-testid="check-coverage-btn"
                className="w-full bg-brand-navy text-white font-heading font-semibold uppercase tracking-wide hover:bg-brand-navy/90 transition-all duration-300 mt-2"
              >
                Check Your Area
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
